import { useState, useEffect, useRef, useMemo } from "react";
import { TalhaoResult } from "features/maps/typings";
import { GoogleMap } from "@react-google-maps/api";
import { DEFAULT_ZOOM, options } from "../base-map/config";
import { useMap } from "../base-map/hooks/useMap";
import { MapTypeId } from "core/features/maps/enums";
import MapControl from "../base-map/components/controls";
import { CurrentViewTypeEnum } from "../base-map/typings";
import { useDroppable } from "@dnd-kit/core";
import ProductivityAndArea from "../base-map/components/productivityAndArea";
import BaseMap from "../base-map";
import BaseMapOperacionalExecucaoCronograma from "../base-map/MapOperacionalExecucaoCronograma"
import MapOperacionalExecucaoOS from "../base-map/MapOperacionalExecucaoOS"
import { TalhaoDetailContextProvider } from "features/maps/talhao/TalhaoDetailContext";
import TalhaoModal from "./components/TalhaoModal";
import { getDefaultIndex } from "./utils";
import { useFilters } from "features/filters/store";
import { useLoading } from "features/global-loading";
import { useMapStore } from "features/maps/store/useMapStore";
import useTalhoesStore from "../base-map/hooks/useTalhoesStore";
import useMapData from "../../../hooks/useMapData";
import useModal from "../../../hooks/useModal";
import useRelatorioContext from "../../../hooks/useRelatorioContext";
import MapOperacionalRelatorioAderencia from "../base-map/MapOperacionalRelatorioAderencia";
import { LegendaRelatorioAderencia } from "./components/LegendaRelatorioAderencia";
import { ResumoPorClasseOperacao } from "./components/ResumoPorClasseOperacao";
import { GestaoOS } from "../../GestaoOS";
import { MapFocusReset } from "../base-map/styles";
import useModalPlanejamentoOperacoes from "../../../hooks/useModalPlanejamentoOperacoes";

interface MapaGeralProps {
  filters: any;
  data: TalhaoResult | undefined;
  dataMap: TalhaoResult | undefined;
  planoOperacionalData: any;
  isFetching: boolean;
  isFetchingMap: boolean;
  refetch: any;
}

function MapaGeral({ filters, data, dataMap, planoOperacionalData, isFetching, isFetchingMap, refetch }: MapaGeralProps) {

  const mapRefPrinter = useRef<HTMLDivElement>(null);

  const { setIsCopyingObject } = useModal();

  const { setPorcentagem, setAderenteExec, setNaoAderenteExec, setMediaDesvio } = useRelatorioContext();

  const { setNodeRef, isOver } = useDroppable({ id: "map-droppable" });

  const [currentViewType, setCurrentViewType] = useState<CurrentViewTypeEnum>(CurrentViewTypeEnum.numberOnly);

  const { filters: { unidade, tipoMapa, tabBarSessaoPlanejamentoOperacional, menuName, menuNameRelatorios, filtersAdvanced } } = useFilters();
  const { safraPeriodo } = filtersAdvanced;

  const { setAllTalhoesIds, setPlanoOperacionalIds } = useTalhoesStore();

  const { updateTalhao, talhao } = useMapStore();
  const setMapInfo = useMapData((s) => s.setMapInfo);

  const points = tabBarSessaoPlanejamentoOperacional === "cadastros" ? data : dataMap;
  const mapCenter = useMemo(() => unidade?.pontoCentral, [unidade]);
  // Execução: cada BaseMap carrega o GeoJSON — useMap só no cadastros (evita sobrescrever estilo).
  const useMapPoints = tabBarSessaoPlanejamentoOperacional === "cadastros" ? data : undefined;

  const { onLoad, onUnmount, mapRef, displayMarkers, zoomAllowsMarkers, hideMarkers, restoreMarkers, zoom, heavyOverlaysEnabled } = useMap({ points: useMapPoints, center: mapCenter });

  const { showLegends } = useModalPlanejamentoOperacoes()

  const {
    legendaAderencia,
    resumoPorOperacao,
    percentualAderencia,
    mediaDesvio,
    aderenteExecutadas,
    naoAderenteExecutadas,
  } = useMemo(() => {
    let percentualAderencia = 0;
    let mediaDesvio = 0;
    let aderenteExecutadas = 0;
    let naoAderenteExecutadas = 0;

    if (dataMap?.Properties?.Aderencia) {
      percentualAderencia = dataMap?.Properties?.Aderencia.Porcentagem ?? 0;
      mediaDesvio = dataMap?.Properties?.Aderencia.MediaDesvioDias ?? 0;
      aderenteExecutadas = dataMap?.Properties?.Aderencia.QuantidadeOperacoesAderentes ?? 0;
      naoAderenteExecutadas = dataMap?.Properties?.Aderencia.QuantidadeOperacoesNaoAderentes ?? 0;
    }

    const semPlanejamento = dataMap?.features?.filter(item => item?.properties?.planosoperacionais && item?.properties?.planosoperacionais.some(plano => plano.Aderente === null));
    const sumAreaSemPlanejamento = semPlanejamento ? semPlanejamento.reduce((acc, item) => acc + Number(item?.properties?.tamanhoAreaHa ?? 0), 0) : 0;

    let countAderente = 0;
    let sumAderente = 0;
    let countNaoAderente = 0;
    let sumNaoAderente = 0;

    dataMap?.features?.forEach(item => {
      const planos = item?.properties?.planosoperacionais;
      if (!planos?.length) return;

      const area = Number(item?.properties?.tamanhoAreaHa ?? 0);
      const qtdAderente = planos.filter(plano => plano.Aderente === true).length;
      const qtdNaoAderente = planos.filter(plano => plano.Aderente === false).length;

      countAderente += qtdAderente;
      sumAderente += area * qtdAderente;

      countNaoAderente += qtdNaoAderente;
      sumNaoAderente += area * qtdNaoAderente;
    });

    const resumoPorOperacao = dataMap?.Properties?.Aderencia?.ResumoPorOperacaoClasse ?? [];

    return {
      legendaAderencia: {
        semPlanejamento: {
          talhoes: semPlanejamento ? semPlanejamento.length : 0,
          somaTalhoes: sumAreaSemPlanejamento.toFixed(2)
        },
        aderente: {
          talhoes: countAderente,
          somaTalhoes: sumAderente.toFixed(2)
        },
        naoAderente: {
          talhoes: countNaoAderente,
          somaTalhoes: sumNaoAderente.toFixed(2)
        }
      },
      resumoPorOperacao: resumoPorOperacao ?? [],
      percentualAderencia,
      mediaDesvio,
      aderenteExecutadas,
      naoAderenteExecutadas,
    };
  }, [dataMap]);

  useEffect(() => {
    setPorcentagem(percentualAderencia);
    setAderenteExec(aderenteExecutadas);
    setNaoAderenteExec(naoAderenteExecutadas);
    setMediaDesvio(mediaDesvio);
  }, [percentualAderencia, aderenteExecutadas, naoAderenteExecutadas, mediaDesvio, setPorcentagem, setAderenteExec, setNaoAderenteExec, setMediaDesvio]);

  useEffect(() => {
    if (dataMap) {
      setMapInfo(dataMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataMap]);

  useLoading(isFetching);
  useLoading(isFetchingMap);

  useEffect(() => {
    if (data) {
      const talhoesIds = data?.features?.map(f => f.properties.id).filter(f => f !== "") ?? [];
      setAllTalhoesIds(talhoesIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (!mapRef.current || !mapCenter) return;
    mapRef.current.panTo({ lat: mapCenter?.lat, lng: mapCenter?.long });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCenter]);

  useEffect(() => {
    setPlanoOperacionalIds([]);
    setIsCopyingObject([]);
    setCurrentViewType(CurrentViewTypeEnum.numberOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabBarSessaoPlanejamentoOperacional]);

  useEffect(() => {
    if (!mapRef.current || menuName === "Gestão de O.S.") return;
    const map = mapRef.current;

    map.data.setStyle(() => ({
      strokeColor: "#fff",
      strokeWeight: 2,
      strokeOpacity: 1,
      fillOpacity: 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuName]);

  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    map.data.forEach((feature: any) => map.data.remove(feature));
    setCurrentViewType(CurrentViewTypeEnum.transparent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (safraPeriodo && tabBarSessaoPlanejamentoOperacional === "cadastros") {
      setCurrentViewType(CurrentViewTypeEnum.numberOnly);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safraPeriodo]);

  return (
    <div
      ref={mapRefPrinter}
      id="map-print"
      style={{
        width: "100%",
        height: "100%",
        outline: "none",
        userSelect: "none",
      }}
    >
      <MapFocusReset />
      <div
        ref={setNodeRef}
        id="map-print-area"
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          opacity: isOver ? 0.5 : 1,
          outline: "none",
          userSelect: "none",
        }}
      >
        <GoogleMap
          mapContainerStyle={{
            width: "100%",
            height: "100%",
            outline: "none",
            userSelect: "none",
          }}
          zoom={DEFAULT_ZOOM}
          onLoad={onLoad}
          onUnmount={() => onUnmount()}
          onRightClick={(e) => {
            e?.domEvent?.preventDefault();
          }}
          mapTypeId={MapTypeId.HYBRID}
          options={options}
        >
          {tabBarSessaoPlanejamentoOperacional === "cadastros" && (
            <BaseMap
              refetch={refetch}
              mapRef={mapRef}
              displayMarkers={displayMarkers}
              zoomAllowsMarkers={zoomAllowsMarkers}
              hideMarkers={hideMarkers}
              restoreMarkers={restoreMarkers}
              currentViewType={currentViewType}
              points={points}
            />
          )}

          {tabBarSessaoPlanejamentoOperacional === "execucao-operacional" && menuName !== "Gestão de O.S." && (
            <BaseMapOperacionalExecucaoCronograma
              mapRef={mapRef}
              displayMarkers={displayMarkers}
              hideMarkers={hideMarkers}
              restoreMarkers={restoreMarkers}
              currentViewType={currentViewType}
              planoOperacionalData={planoOperacionalData}
              points={points}
              zoom={zoom}
              heavyOverlaysEnabled={heavyOverlaysEnabled}
              zoomAllowsMarkers={zoomAllowsMarkers}
            />
          )}

          {tabBarSessaoPlanejamentoOperacional === "execucao-operacional" && menuName === "Gestão de O.S." && (
            <MapOperacionalExecucaoOS
              mapRef={mapRef}
              displayMarkers={displayMarkers}
              currentViewType={currentViewType}
              planoOperacionalData={planoOperacionalData}
              points={points}
            />
          )}
          {tabBarSessaoPlanejamentoOperacional === 'relatorios' && menuNameRelatorios === "Relatório de Aderência Operacional" && (
            <MapOperacionalRelatorioAderencia
              mapRef={mapRef}
              displayMarkers={displayMarkers}
              currentViewType={currentViewType}
              points={points}
            />
          )}
        </GoogleMap>
      </div>

      {mapRef.current && (
        <MapControl
          center={mapCenter!}
          map={mapRef.current}
          currentViewType={currentViewType}
          handleChangeCurrentViewType={setCurrentViewType}
          mapRefPrinter={mapRefPrinter}
        />
      )}

      {tabBarSessaoPlanejamentoOperacional === 'relatorios' && menuNameRelatorios === "Relatório de Aderência Operacional" && (
        <>
          <LegendaRelatorioAderencia legendaAderencia={legendaAderencia} />
          <ResumoPorClasseOperacao resumoPorOperacaoClasse={resumoPorOperacao} />
        </>
      )}

      {tabBarSessaoPlanejamentoOperacional === 'execucao-operacional' && menuName === "Gestão de O.S." && showLegends && (
        <>
          <GestaoOS />
        </>
      )}

      <ProductivityAndArea
        Properties={points?.Properties}
        isLoading={isFetching || isFetchingMap}
      />

      {talhao && (
        <TalhaoDetailContextProvider defaultIndex={getDefaultIndex(tipoMapa)}>
          <TalhaoModal
            talhao={talhao}
            onClose={() => updateTalhao(undefined)}
          />
        </TalhaoDetailContextProvider>
      )}
    </div>
  );
}

export default MapaGeral;
